import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Save, ArrowLeft, Plus, Trash2, Upload, X, MapPin, Star } from "lucide-react";

import useDocumentTitle from '../../hooks/useDocumentTitle';
import ShimmerLoader from '../../hooks/ShimmerLoader';
// import { useAuth } from "../../context/AuthContext";

import {
    getProductInfo,
    updateProductPost,
    getProductDetails,
    saveProductDetails,
    getProductImages,
    saveProductImages,
    getProductAddresses,
    saveProductAddresses
} from "../../services/api";

export default function ProductDetails() {
    // const { user } = useAuth();
    const { id } = useParams();
    const navigate = useNavigate();

    useDocumentTitle(`Product Details - ${id}`);

    // States for Product Info Tab (NEW)
    const [productInfo, setProductInfo] = useState({
        product_id: id,
        product_name: '',
        product_type: '',
        total_qty: '',
        buying_price: '',
        selling_price: ''
    });
    const [productInfoErrors, setProductInfoErrors] = useState({});
    const [submittingProductInfo, setSubmittingProductInfo] = useState(false);
    const [producttypelist, setProductTypeList] = useState([]);
    const [productInfoLoading, setProductInfoLoading] = useState(false);

    // States for Product Details Tab
    const [productDetails, setProductDetails] = useState([]);
    const [detailsLoading, setDetailsLoading] = useState(true);
    const [formData, setFormData] = useState([]);
    const [formErrors, setFormErrors] = useState([]);
    const [submittingDetails, setSubmittingDetails] = useState(false);
    const [deletedIds, setDeletedIds] = useState([]);

    // States for Product Images Tab
    const [productImages, setProductImages] = useState([]);
    const [imagesLoading, setImagesLoading] = useState(true);
    const [imagesFormData, setImagesFormData] = useState([]);
    const [imagesErrors, setImagesErrors] = useState([]);
    const [submittingImages, setSubmittingImages] = useState(false);
    const [deletedImageIds, setDeletedImageIds] = useState([]);

    // States for Product Address Tab
    const [productAddresses, setProductAddresses] = useState([]);
    const [addressLoading, setAddressLoading] = useState(true);
    const [addressFormData, setAddressFormData] = useState([]);
    const [addressErrors, setAddressErrors] = useState([]);
    const [submittingAddress, setSubmittingAddress] = useState(false);
    const [deletedAddressIds, setDeletedAddressIds] = useState([]);

    // Toast states
    const [toast, setToast] = useState({ show: false, message: '', type: 'error' });

    // Refs for scrolling to new cards
    const newDetailsCardRef = useRef(null);
    const newImagesCardRef = useRef(null);
    const newAddressCardRef = useRef(null);

    // Tab configuration - ADDED ProductInfo as first tab
    const tabData = [
        {
            id: "ProductInfo",
            label: "Product Info",
            content: ''
        },
        {
            id: "ProductDetails",
            label: "Product Details",
            content: ''
        },
        {
            id: "ProductImages",
            label: "Product Images",
            content: ''
        },
        {
            id: "ProductAddress",
            label: "Product Pickup Address",
            content: ''
        },
    ];

    const [activeTab, setActiveTab] = useState('ProductDetails');

    // Toast functions
    const showToast = (message, type = 'error') => {
        setToast({ show: true, message, type });
        setTimeout(() => {
            setToast(prev => ({ ...prev, show: false }));
        }, 3000);
    };

    const hideToast = () => {
        setToast({ show: false, message: '', type: 'error' });
    };

    // Fetch data based on active tab
    useEffect(() => {
        if (id) {
            if (activeTab === 'ProductInfo') {
                // You can add product info fetch logic here if needed
                fetchProductInfo();
            } else if (activeTab === 'ProductDetails') {
                fetchProductDetails();
            } else if (activeTab === 'ProductImages') {
                fetchProductImages();
            } else if (activeTab === 'ProductAddress') {
                fetchProductAddresses();
            }
        }
    }, [id, activeTab]);

    // Scroll to newly added cards
    useEffect(() => {
        if (newDetailsCardRef.current !== null) {
            scrollToAndHighlightCard('details', newDetailsCardRef.current);
            newDetailsCardRef.current = null;
        }
    }, [formData]);

    useEffect(() => {
        if (newImagesCardRef.current !== null) {
            scrollToAndHighlightCard('images', newImagesCardRef.current);
            newImagesCardRef.current = null;
        }
    }, [imagesFormData]);

    useEffect(() => {
        if (newAddressCardRef.current !== null) {
            scrollToAndHighlightCard('address', newAddressCardRef.current);
            newAddressCardRef.current = null;
        }
    }, [addressFormData]);

    // Function to scroll to and highlight cards
    const scrollToAndHighlightCard = (type, index) => {
        setTimeout(() => {
            const cards = document.querySelectorAll(`[data-card-type="${type}"]`);
            const targetCard = cards[index];
            if (targetCard) {
                // Add highlight class
                targetCard.classList.add('ring-4', 'ring-[#f4a406]', 'ring-opacity-70', 'transition-all', 'duration-300');

                // Scroll to card
                targetCard.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });

                // Remove highlight after 2 seconds
                setTimeout(() => {
                    targetCard.classList.remove('ring-4', 'ring-[#f4a406]', 'ring-opacity-70');
                }, 2000);
            }
        }, 100);
    };

    // ========== PRODUCT INFO TAB FUNCTIONS (NEW) ==========
    const fetchProductInfo = async () => {
        try {
            setProductInfoLoading(true);
            const response = await getProductInfo(id);
            const details = response.data || [];
            // console.log(details.length);
            setProductTypeList(response.producttypelist || [])
            if (details) {
                setProductInfo({
                    product_id: details.id,
                    product_name: details.product_name,
                    product_type: details.product_type,
                    total_qty: details.total_qty,
                    buying_price: details.buying_price,
                    selling_price: details.selling_price
                });
            } else {
                setProductInfo({
                    product_name: '',
                    product_type: '',
                    total_qty: '',
                    buying_price: '',
                    selling_price: ''
                });
            }
            setProductInfoLoading(false)
            setFormErrors(details);
            setDeletedIds([]);
        } catch (err) {
            showToast(err.message || 'Failed to fetch product types', 'error');
        }
    };

    const handleProductInfoChange = (field, value) => {
        setProductInfo(prev => ({
            ...prev,
            [field]: value
        }));

        if (productInfoErrors[field]) {
            setProductInfoErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateProductInfo = () => {
        const errors = {};

        if (!productInfo.product_name.trim()) {
            errors.product_name = 'Product Name is required';
        }

        if (!productInfo.product_type) {
            errors.product_type = 'Product Type is required';
        }

        if (!productInfo.total_qty || productInfo.total_qty < 0) {
            errors.total_qty = 'Total Quantity is required and must be non-negative';
        }

        if (!productInfo.buying_price || productInfo.buying_price < 0) {
            errors.buying_price = 'Buying GT coins is required and must be non-negative';
        }

        if (!productInfo.selling_price || productInfo.selling_price < 0) {
            errors.selling_price = 'Selling GT coins is required and must be non-negative';
        }

        setProductInfoErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleProductInfoSubmit = async (e) => {
        e.preventDefault();

        if (!validateProductInfo()) {
            showToast('Please fill all required fields', 'error');
            return;
        }

        setSubmittingProductInfo(true);

        try {

            const response = await updateProductPost(productInfo);

            if (response.status && response.status.success === true) {
                showToast('Successfully updated product info', 'success');
            } else {
                showToast(response.message || 'Failed to save product info', 'error');
            }

            showToast('Product Info saved successfully!', 'success');

        } catch (err) {
            showToast(err.message || 'Failed to save product info', 'error');
        } finally {
            setSubmittingProductInfo(false);
        }
    };

    // ========== PRODUCT DETAILS TAB FUNCTIONS ==========
    const fetchProductDetails = async () => {
        try {
            setDetailsLoading(true);
            const response = await getProductDetails(id);
            const details = response.data.data || [];
            setProductDetails(details);

            if (details.length > 0) {
                setFormData(details.map(detail => ({
                    id: detail.id,
                    products_id: id,
                    title: detail.title || '',
                    description: detail.description || '',
                    order_by: detail.order_by || 0
                })));
            } else {
                setFormData([{
                    products_id: id,
                    title: '',
                    description: '',
                    order_by: 1
                }]);
            }
            setFormErrors(details.map(() => ({})));
            setDeletedIds([]);
        } catch (err) {
            showToast(err.message || 'Failed to fetch product details', 'error');
        } finally {
            setDetailsLoading(false);
        }
    };

    const addNewForm = () => {
        const newIndex = formData.length;
        newDetailsCardRef.current = newIndex;

        setFormData(prev => [
            ...prev,
            {
                products_id: id,
                title: '',
                description: '',
                order_by: prev.length > 0 ? Math.max(...prev.map(item => item.order_by)) + 1 : 1
            }
        ]);
        setFormErrors(prev => [...prev, {}]);
    };

    const removeForm = (index) => {
        const formToRemove = formData[index];
        if (formToRemove.id) {
            setDeletedIds(prev => [...prev, formToRemove.id]);
        }
        setFormData(prev => prev.filter((_, i) => i !== index));
        setFormErrors(prev => prev.filter((_, i) => i !== index));
    };

    const handleInputChange = (index, field, value) => {
        setFormData(prev => {
            const newData = [...prev];
            newData[index] = {
                ...newData[index],
                [field]: value
            };
            return newData;
        });

        if (formErrors[index]?.[field]) {
            setFormErrors(prev => {
                const newErrors = [...prev];
                newErrors[index] = {
                    ...newErrors[index],
                    [field]: ''
                };
                return newErrors;
            });
        }
    };

    const validateDetailsForms = () => {
        const errors = [];
        let isValid = true;

        formData.forEach((form, index) => {
            const formErrors = {};

            if (!form.title.trim()) {
                formErrors.title = 'Title is required';
                isValid = false;
            }

            if (!form.description.trim()) {
                formErrors.description = 'Description is required';
                isValid = false;
            }

            errors[index] = formErrors;
        });

        setFormErrors(errors);
        return isValid;
    };

    const handleDetailsSubmit = async (e) => {
        e.preventDefault();

        if (!validateDetailsForms()) {
            showToast('Please fill all required fields', 'error');
            return;
        }

        setSubmittingDetails(true);

        try {
            const payload = {
                products: formData,
                deleted_ids: deletedIds
            };

            const response = await saveProductDetails(id, payload);

            if (response.status && response.status.success === true) {
                showToast('Successfully updated product details', 'success');
                await fetchProductDetails();
            } else {
                showToast(response.message || 'Failed to save product details', 'error');
            }

        } catch (err) {
            showToast(err.message || 'Failed to save product details', 'error');
        } finally {
            setSubmittingDetails(false);
        }
    };

    // ========== PRODUCT IMAGES TAB FUNCTIONS ==========
    const fetchProductImages = async () => {
        try {
            setImagesLoading(true);
            const response = await getProductImages(id);
            const images = response.data.data || [];
            setProductImages(images);

            if (images.length > 0) {
                setImagesFormData(images.map(image => ({
                    id: image.id,
                    products_id: id,
                    title: image.title || '',
                    image_url: image.images || '',
                    file: null,
                    isExisting: true,
                    is_primary: image.is_primary || false
                })));
            } else {
                setImagesFormData([{
                    products_id: id,
                    title: '',
                    image_url: '',
                    file: null,
                    isExisting: false,
                    is_primary: true // First image should be primary by default
                }]);
            }
            setImagesErrors(images.map(() => ({})));
            setDeletedImageIds([]);
        } catch (err) {
            showToast(err.message || 'Failed to fetch product images', 'error');
        } finally {
            setImagesLoading(false);
        }
    };

    const addNewImageForm = () => {
        const newIndex = imagesFormData.length;
        newImagesCardRef.current = newIndex;

        setImagesFormData(prev => [
            ...prev,
            {
                products_id: id,
                title: '',
                image_url: '',
                file: null,
                isExisting: false,
                is_primary: false // New images are not primary by default
            }
        ]);
        setImagesErrors(prev => [...prev, {}]);
    };

    const removeImageForm = (index) => {
        const formToRemove = imagesFormData[index];
        if (formToRemove.id) {
            setDeletedImageIds(prev => [...prev, formToRemove.id]);
        }
        setImagesFormData(prev => prev.filter((_, i) => i !== index));
        setImagesErrors(prev => prev.filter((_, i) => i !== index));
    };

    const handleImageInputChange = (index, field, value) => {
        setImagesFormData(prev => {
            const newData = [...prev];

            // If setting as primary, ensure only one image is primary
            if (field === 'is_primary' && value === true) {
                // Set all other images to false
                newData.forEach((item, i) => {
                    if (i !== index) {
                        newData[i] = { ...newData[i], is_primary: false };
                    }
                });
            }

            newData[index] = {
                ...newData[index],
                [field]: value
            };
            return newData;
        });

        if (imagesErrors[index]?.[field]) {
            setImagesErrors(prev => {
                const newErrors = [...prev];
                newErrors[index] = {
                    ...newErrors[index],
                    [field]: ''
                };
                return newErrors;
            });
        }
    };

    const handleImageUpload = (index, file) => {
        if (!file) return;

        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            showToast('Please select a valid image file (JPEG, PNG, GIF, WebP)', 'error');
            return;
        }

        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            showToast('Image size should be less than 5MB', 'error');
            return;
        }

        setImagesFormData(prev => {
            const newData = [...prev];
            newData[index] = {
                ...newData[index],
                file: file,
                image_url: URL.createObjectURL(file),
                isExisting: false
            };
            return newData;
        });

        if (imagesErrors[index]?.image) {
            setImagesErrors(prev => {
                const newErrors = [...prev];
                newErrors[index] = { ...newErrors[index], image: '' };
                return newErrors;
            });
        }
    };

    const removeImage = (index) => {
        setImagesFormData(prev => {
            const newData = [...prev];
            newData[index] = {
                ...newData[index],
                file: null,
                image_url: '',
                isExisting: false
            };
            return newData;
        });
    };

    const validateImagesForms = () => {
        const errors = [];
        let isValid = true;

        imagesFormData.forEach((form, index) => {
            const formErrors = {};

            if (!form.title.trim()) {
                formErrors.title = 'Title is required';
                isValid = false;
            }

            if (!form.image_url) {
                formErrors.image = 'Image is required';
                isValid = false;
            }

            // Ensure at least one image is primary
            const hasPrimary = imagesFormData.some(img => img.is_primary == true);
            if (!hasPrimary) {
                if (index === 0) { // Show error on first form if no primary
                    formErrors.is_primary = 'At least one image must be set as primary';
                    isValid = false;
                }
            }

            errors[index] = formErrors;
        });

        setImagesErrors(errors);
        return isValid;
    };

    const handleImagesSubmit = async (e) => {
        e.preventDefault();

        if (!validateImagesForms()) {
            showToast('Please fill all required fields, upload images and ensure one image is primary', 'error');
            return;
        }

        setSubmittingImages(true);

        try {
            const formDataToSend = new FormData();

            imagesFormData.forEach((image, index) => {
                if (image.file) {
                    formDataToSend.append(`cards[${index}][image]`, image.file);
                }
                formDataToSend.append(`cards[${index}][title]`, image.title);
                formDataToSend.append(`cards[${index}][is_primary]`, image.is_primary ? '1' : '0');

                if (image.id) {
                    formDataToSend.append(`cards[${index}][id]`, image.id);
                }

                if (image.image_url && !image.file) {
                    formDataToSend.append(`cards[${index}][existing_image_url]`, image.image_url);
                }
            });

            deletedImageIds.forEach(id => {
                formDataToSend.append('deleted_ids[]', id);
            });

            formDataToSend.append('products_id', id);

            const response = await saveProductImages(id, formDataToSend);

            if (response.data.status?.success) {
                showToast('Successfully updated product images', 'success');
                await fetchProductImages();
            } else {
                showToast(response.data.message || 'Failed to save product images', 'error');
            }

        } catch (err) {
            console.error('Image upload error:', err);
            showToast(err.response?.data?.message || err.message || 'Failed to save product images', 'error');
        } finally {
            setSubmittingImages(false);
        }
    };

    // ========== PRODUCT ADDRESS TAB FUNCTIONS ==========
    const fetchProductAddresses = async () => {
        try {
            setAddressLoading(true);
            const response = await getProductAddresses(id);
            const addresses = response.data.data || [];
            setProductAddresses(addresses);

            if (addresses.length > 0) {
                setAddressFormData(addresses.map(address => ({
                    id: address.id,
                    products_id: id,
                    title: address.title || '',
                    address: address.address || '',
                    latitude: address.latitude || '',
                    longitude: address.longitude || '',
                    is_primary: address.is_primary || false
                })));
            } else {
                setAddressFormData([{
                    products_id: id,
                    title: '',
                    address: '',
                    is_primary: true // First address should be primary by default
                }]);
            }
            setAddressErrors(addresses.map(() => ({})));
            setDeletedAddressIds([]);
        } catch (err) {
            showToast(err.message || 'Failed to fetch product addresses', 'error');
        } finally {
            setAddressLoading(false);
        }
    };

    const addNewAddressForm = () => {
        const newIndex = addressFormData.length;
        newAddressCardRef.current = newIndex;

        setAddressFormData(prev => [
            ...prev,
            {
                products_id: id,
                title: '',
                address: '',
                latitude: '',
                longitude: '',
                is_primary: false // New addresses are not primary by default
            }
        ]);
        setAddressErrors(prev => [...prev, {}]);
    };

    const removeAddressForm = (index) => {
        const formToRemove = addressFormData[index];
        if (formToRemove.id) {
            setDeletedAddressIds(prev => [...prev, formToRemove.id]);
        }
        setAddressFormData(prev => prev.filter((_, i) => i !== index));
        setAddressErrors(prev => prev.filter((_, i) => i !== index));
    };

    const handleAddressInputChange = (index, field, value) => {
        setAddressFormData(prev => {
            const newData = [...prev];

            // If setting as primary, ensure only one address is primary
            if (field === 'is_primary' && value === true) {
                // Set all other addresses to false
                newData.forEach((item, i) => {
                    if (i !== index) {
                        newData[i] = { ...newData[i], is_primary: false };
                    }
                });
            }

            newData[index] = {
                ...newData[index],
                [field]: value
            };
            return newData;
        });

        if (addressErrors[index]?.[field]) {
            setAddressErrors(prev => {
                const newErrors = [...prev];
                newErrors[index] = {
                    ...newErrors[index],
                    [field]: ''
                };
                return newErrors;
            });
        }
    };

    const validateAddressForms = () => {
        const errors = [];
        let isValid = true;

        addressFormData.forEach((form, index) => {
            const formErrors = {};

            if (!form.title.trim()) {
                formErrors.title = 'Title is required';
                isValid = false;
            }

            if (!form.address.trim()) {
                formErrors.address = 'Address is required';
                isValid = false;
            }
            if (form.latitude && isNaN(form.latitude)) {
                formErrors.latitude = 'Latitude must be a valid number';
                isValid = false;
            }

            if (form.longitude && isNaN(form.longitude)) {
                formErrors.longitude = 'Longitude must be a valid number';
                isValid = false;
            }
            // Ensure at least one address is primary
            const hasPrimary = addressFormData.some(addr => addr.is_primary == true);
            if (!hasPrimary) {
                if (index === 0) { // Show error on first form if no primary
                    formErrors.is_primary = 'At least one address must be set as primary';
                    isValid = false;
                }
            }

            errors[index] = formErrors;
        });

        setAddressErrors(errors);
        return isValid;
    };

    const handleAddressSubmit = async (e) => {
        e.preventDefault();

        if (!validateAddressForms()) {
            showToast('Please fill all required fields and ensure one address is primary', 'error');
            return;
        }

        setSubmittingAddress(true);

        try {
            const payload = {
                addresses: addressFormData,
                deleted_ids: deletedAddressIds
            };

            const response = await saveProductAddresses(id, payload);

            if (response.data.status && response.data.status.success === true) {
                showToast('Successfully updated product addresses', 'success');
                await fetchProductAddresses();
            } else {
                showToast(response.data.message || 'Failed to save product addresses', 'error');
            }

        } catch (err) {
            showToast(err.message || 'Failed to save product addresses', 'error');
        } finally {
            setSubmittingAddress(false);
        }
    };

    // Go back to products list
    const handleGoBack = () => {
        navigate('/products');
    };

    // Clean up blob URLs when component unmounts
    useEffect(() => {
        return () => {
            imagesFormData.forEach(image => {
                if (image.image_url && image.image_url.startsWith('blob:')) {
                    URL.revokeObjectURL(image.image_url);
                }
            });
        };
    }, [imagesFormData]);

    return (
        <>
            {/* Toast Notification */}
            {toast.show && (
                <div className={`fixed top-4 right-4 ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 min-w-80 z-50 transition-all duration-300 ${toast.show ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}>
                    <span className="flex-1">{toast.message}</span>
                    <button onClick={hideToast} className="text-white hover:text-gray-200 transition-colors">
                        ✕
                    </button>
                </div>
            )}

            <div className="flex flex-col gap-6">
                <div className="bg-white shadow-lg rounded-2xl p-6">
                    <div className="w-full">
                        {/* Tab Header */}
                        <div className="flex border-b border-gray-200">
                            {tabData.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`relative px-4 py-3 text-sm font-medium transition-all
                                            ${activeTab === tab.id
                                            ? "text-blue-600"
                                            : "text-gray-600 hover:text-gray-900"
                                        }
                                            `}
                                >
                                    {tab.label}

                                    {activeTab === tab.id && (
                                        <span className="absolute bottom-0 left-0 w-full h-[2px] bg-blue-600 rounded-full"></span>
                                    )}
                                </button>
                            ))}
                        </div>

                        {/* Tab Content */}
                        <div className="mt-4">
                            {/* PRODUCT INFO TAB (NEW) */}
                            {activeTab === 'ProductInfo' && (
                                <div key={'ProductInfo'} className="text-gray-700">
                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                        <div className="flex items-center gap-4">
                                            <button
                                                onClick={handleGoBack}
                                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                            >
                                                <ArrowLeft size={20} />
                                            </button>
                                            <div>
                                                <h1 className="text-2xl font-bold text-gray-900">
                                                    Product Information
                                                </h1>
                                            </div>
                                        </div>
                                    </div>
                                    {productInfoLoading ? (
                                        <div className="bg-white shadow-lg rounded-2xl p-6">
                                            <div className="space-y-4">
                                                <ShimmerLoader />
                                                <ShimmerLoader />
                                                <ShimmerLoader />
                                            </div>
                                        </div>
                                    ) : (
                                        <form onSubmit={handleProductInfoSubmit} className="mt-6">
                                            <div className="bg-white shadow-lg rounded-2xl p-6">
                                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                                    {/* Product Name */}
                                                    <div className="md:col-span-2">
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Product Name *
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="product_name"
                                                            value={productInfo.product_name}
                                                            onChange={(e) => handleProductInfoChange('product_name', e.target.value)}
                                                            className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#f4a406] focus:border-transparent ${productInfoErrors.product_name ? 'border-red-500' : 'border-gray-300'
                                                                }`}
                                                            placeholder="Enter product name"
                                                            disabled={submittingProductInfo}
                                                        />
                                                        {productInfoErrors.product_name && (
                                                            <p className="text-red-500 text-xs mt-1">{productInfoErrors.product_name}</p>
                                                        )}
                                                    </div>

                                                    {/* Product Type Dropdown */}
                                                    <div className="md:col-span-2">
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Product Type *
                                                        </label>
                                                        <select
                                                            name="product_type"
                                                            value={productInfo.product_type}
                                                            onChange={(e) => handleProductInfoChange('product_type', e.target.value)}
                                                            className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#f4a406] focus:border-transparent ${productInfoErrors.product_type ? 'border-red-500' : 'border-gray-300'
                                                                }`}
                                                            disabled={submittingProductInfo}
                                                        >
                                                            <option value="">Select Product Type</option>
                                                            {producttypelist && producttypelist.map((item, index) =>
                                                                <option key={item.id || index} value={item.id}>{item.name}</option>
                                                            )}
                                                        </select>
                                                        {productInfoErrors.product_type && (
                                                            <p className="text-red-500 text-xs mt-1">{productInfoErrors.product_type}</p>
                                                        )}
                                                    </div>

                                                    {/* Total Quantity */}
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Total Quantity *
                                                        </label>
                                                        <input
                                                            type="number"
                                                            name="total_qty"
                                                            value={productInfo.total_qty}
                                                            onChange={(e) => handleProductInfoChange('total_qty', e.target.value)}
                                                            min="0"
                                                            className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#f4a406] focus:border-transparent ${productInfoErrors.total_qty ? 'border-red-500' : 'border-gray-300'
                                                                }`}
                                                            placeholder="Enter total quantity"
                                                            disabled={submittingProductInfo}
                                                        />
                                                        {productInfoErrors.total_qty && (
                                                            <p className="text-red-500 text-xs mt-1">{productInfoErrors.total_qty}</p>
                                                        )}
                                                    </div>

                                                    {/* Buying Price */}
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Buying GT coins *
                                                        </label>
                                                        <div className="relative">
                                                            <span className="absolute left-3 top-2 text-gray-500"></span>
                                                            <input
                                                                type="number"
                                                                name="buying_price"
                                                                value={productInfo.buying_price}
                                                                onChange={(e) => handleProductInfoChange('buying_price', e.target.value)}
                                                                min="0"
                                                                step="0.01"
                                                                className={`w-full p-2 pl-8 border rounded-lg focus:ring-2 focus:ring-[#f4a406] focus:border-transparent ${productInfoErrors.buying_price ? 'border-red-500' : 'border-gray-300'
                                                                    }`}
                                                                placeholder="0.00"
                                                                disabled={submittingProductInfo}
                                                            />
                                                        </div>
                                                        {productInfoErrors.buying_price && (
                                                            <p className="text-red-500 text-xs mt-1">{productInfoErrors.buying_price}</p>
                                                        )}
                                                    </div>

                                                    {/* Selling Price */}
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Selling GT coins *
                                                        </label>
                                                        <div className="relative">
                                                            <span className="absolute left-3 top-2 text-gray-500"></span>
                                                            <input
                                                                type="number"
                                                                name="selling_price"
                                                                value={productInfo.selling_price}
                                                                onChange={(e) => handleProductInfoChange('selling_price', e.target.value)}
                                                                min="0"
                                                                step="0.01"
                                                                className={`w-full p-2 pl-8 border rounded-lg focus:ring-2 focus:ring-[#f4a406] focus:border-transparent ${productInfoErrors.selling_price ? 'border-red-500' : 'border-gray-300'
                                                                    }`}
                                                                placeholder="0.00"
                                                                disabled={submittingProductInfo}
                                                            />
                                                        </div>
                                                        {productInfoErrors.selling_price && (
                                                            <p className="text-red-500 text-xs mt-1">{productInfoErrors.selling_price}</p>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="flex gap-3 mt-6">
                                                    <button
                                                        type="button"
                                                        onClick={handleGoBack}
                                                        className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                                        disabled={submittingProductInfo}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        type="submit"
                                                        disabled={submittingProductInfo}
                                                        className="flex-1 py-2 px-4 bg-[#f4a406] text-white rounded-lg hover:bg-[#d89005] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {submittingProductInfo ? 'Saving...' : 'Save Product Info'}
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    )}
                                </div>
                            )}

                            {/* PRODUCT DETAILS TAB */}
                            {activeTab === 'ProductDetails' && (
                                <div key={'ProductDetails'} className="text-gray-700">
                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                        <div className="flex items-center gap-4">
                                            <button
                                                onClick={handleGoBack}
                                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                            >
                                                <ArrowLeft size={20} />
                                            </button>
                                            <div>
                                                <h1 className="text-2xl font-bold text-gray-900">
                                                    Product Details
                                                </h1>
                                            </div>
                                        </div>
                                        <button
                                            onClick={addNewForm}
                                            className="bg-[#f4a406] hover:bg-[#d89005] text-white py-2 px-4 rounded-lg transition-colors inline-flex items-center gap-2"
                                        >
                                            <Plus size={18} />
                                            Add New
                                        </button>
                                    </div>

                                    {/* All Forms */}
                                    {detailsLoading ? (
                                        <div className="bg-white shadow-lg rounded-2xl p-6">
                                            <div className="space-y-4">
                                                <ShimmerLoader />
                                                <ShimmerLoader />
                                                <ShimmerLoader />
                                            </div>
                                        </div>
                                    ) : (
                                        <form onSubmit={handleDetailsSubmit}>
                                            <div className="space-y-6 mt-4">
                                                {formData.map((form, index) => (
                                                    <div
                                                        key={form.id || `new-${index}`}
                                                        className="bg-white shadow-lg rounded-2xl p-6 relative border-2 border-gray-200 transition-all duration-300"
                                                        data-card-type="details"
                                                    >
                                                        {/* Delete button */}
                                                        {formData.length > 1 && <button
                                                            type="button"
                                                            onClick={() => removeForm(index)}
                                                            disabled={submittingDetails}
                                                            className="absolute -top-2 -right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>}

                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                            {/* Title */}
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                    Title*
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    value={form.title}
                                                                    onChange={(e) => handleInputChange(index, 'title', e.target.value)}
                                                                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#f4a406] focus:border-transparent ${formErrors[index]?.title ? 'border-red-500' : 'border-gray-300'
                                                                        }`}
                                                                    placeholder="Enter product title"
                                                                    disabled={submittingDetails}
                                                                />
                                                                {formErrors[index]?.title && (
                                                                    <p className="text-red-500 text-xs mt-1">{formErrors[index].title}</p>
                                                                )}
                                                            </div>

                                                            {/* Order By */}
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                    Order By*
                                                                </label>
                                                                <input
                                                                    type="number"
                                                                    value={form.order_by}
                                                                    onChange={(e) => handleInputChange(index, 'order_by', parseInt(e.target.value) || 0)}
                                                                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#f4a406] focus:border-transparent ${formErrors[index]?.order_by ? 'border-red-500' : 'border-gray-300'
                                                                        }`}
                                                                    placeholder="Enter Order by"
                                                                    disabled={submittingDetails}
                                                                />
                                                                {formErrors[index]?.order_by && (
                                                                    <p className="text-red-500 text-xs mt-1">{formErrors[index].order_by}</p>
                                                                )}
                                                            </div>

                                                            {/* Description - Full width */}
                                                            <div className="md:col-span-2">
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                    Description*
                                                                </label>
                                                                <textarea
                                                                    value={form.description}
                                                                    onChange={(e) => handleInputChange(index, 'description', e.target.value)}
                                                                    rows={3}
                                                                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#f4a406] focus:border-transparent ${formErrors[index]?.description ? 'border-red-500' : 'border-gray-300'
                                                                        }`}
                                                                    placeholder="Enter product description"
                                                                    disabled={submittingDetails}
                                                                />
                                                                {formErrors[index]?.description && (
                                                                    <p className="text-red-500 text-xs mt-1">{formErrors[index].description}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Submit Button */}
                                            {(formData.length > 0 || deletedIds.length > 0) && (
                                                <div className="flex justify-end mt-6">
                                                    <button
                                                        type="submit"
                                                        disabled={submittingDetails}
                                                        className="bg-[#f4a406] hover:bg-[#d89005] text-white py-3 px-8 rounded-lg transition-colors inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        <Save size={18} />
                                                        {submittingDetails ? 'Saving...' : `Save Changes`}
                                                    </button>
                                                </div>
                                            )}
                                        </form>
                                    )}
                                </div>
                            )}

                            {/* PRODUCT IMAGES TAB */}
                            {activeTab === 'ProductImages' && (
                                <div key={'ProductImages'} className="text-gray-700">
                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                        <div className="flex items-center gap-4">
                                            <button
                                                onClick={handleGoBack}
                                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                            >
                                                <ArrowLeft size={20} />
                                            </button>
                                            <div>
                                                <h1 className="text-2xl font-bold text-gray-900">
                                                    Product Images
                                                </h1>
                                            </div>
                                        </div>
                                        <button
                                            onClick={addNewImageForm}
                                            className="bg-[#f4a406] hover:bg-[#d89005] text-white py-2 px-4 rounded-lg transition-colors inline-flex items-center gap-2"
                                        >
                                            <Plus size={18} />
                                            Add New Image
                                        </button>
                                    </div>

                                    {imagesLoading ? (
                                        <div className="bg-white shadow-lg rounded-2xl p-6">
                                            <div className="space-y-4">
                                                <ShimmerLoader />
                                                <ShimmerLoader />
                                                <ShimmerLoader />
                                            </div>
                                        </div>
                                    ) : (
                                        <form onSubmit={handleImagesSubmit}>
                                            <div className="space-y-6 mt-4">
                                                {imagesFormData.map((image, index) => (
                                                    <div
                                                        key={image.id || `new-image-${index}`}
                                                        className="bg-white shadow-lg rounded-2xl p-6 relative border-2 border-gray-200 transition-all duration-300"
                                                        data-card-type="images"
                                                    >
                                                        {/* Header section with delete button and badge */}
                                                        <div className="flex justify-between items-center mb-6">
                                                            {/* Delete button - Don't show if it's the only image */}
                                                            {imagesFormData.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeImageForm(index)}
                                                                    disabled={submittingImages}
                                                                    className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            )}

                                                            {/* Spacer to push badge to right when delete button is hidden */}
                                                            {imagesFormData.length <= 1 && <div></div>}

                                                            {/* Primary Image Badge */}
                                                            {image.is_primary && (
                                                                <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1">
                                                                    <Star size={12} fill="currentColor" />
                                                                    Primary Image
                                                                </span>
                                                            )}
                                                        </div>

                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                            {/* Title */}
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                    Image Title*
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    value={image.title}
                                                                    onChange={(e) => handleImageInputChange(index, 'title', e.target.value)}
                                                                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#f4a406] focus:border-transparent ${imagesErrors[index]?.title ? 'border-red-500' : 'border-gray-300'
                                                                        }`}
                                                                    placeholder="Enter image title"
                                                                    disabled={submittingImages}
                                                                />
                                                                {imagesErrors[index]?.title && (
                                                                    <p className="text-red-500 text-xs mt-1">{imagesErrors[index].title}</p>
                                                                )}
                                                            </div>

                                                            {/* Is Primary */}
                                                            <div className="flex items-center">
                                                                <label className="flex items-center cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={image.is_primary}
                                                                        onChange={(e) => handleImageInputChange(index, 'is_primary', e.target.checked)}
                                                                        disabled={submittingImages}
                                                                        className="hidden"
                                                                    />
                                                                    <div className={`w-6 h-6 border-2 rounded-md mr-3 flex items-center justify-center transition-colors ${image.is_primary ? 'bg-[#f4a406] border-[#f4a406]' : 'border-gray-300'}`}>
                                                                        {image.is_primary && (
                                                                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                                                                            </svg>
                                                                        )}
                                                                    </div>
                                                                    <span className="text-sm font-medium text-gray-700">
                                                                        Primary Image
                                                                    </span>
                                                                </label>
                                                            </div>

                                                            {/* Image Upload - Full width */}
                                                            <div className="md:col-span-2">
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                    Image*
                                                                </label>

                                                                {image.image_url ? (
                                                                    <div className="flex items-center gap-4">
                                                                        <div className="relative">
                                                                            <img
                                                                                src={image.image_url}
                                                                                alt={image.title || 'Product image'}
                                                                                className="h-32 w-auto rounded-lg border border-gray-300 object-cover"
                                                                                onError={(e) => {
                                                                                    e.target.onerror = null;
                                                                                    e.target.src = "/notfound.jpg";
                                                                                }}
                                                                            />
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => removeImage(index)}
                                                                                disabled={submittingImages}
                                                                                className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors disabled:opacity-50"
                                                                            >
                                                                                <X size={14} />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                                                                        <input
                                                                            type="file"
                                                                            accept="image/*"
                                                                            onChange={(e) => handleImageUpload(index, e.target.files[0])}
                                                                            className="hidden"
                                                                            id={`image-upload-${index}`}
                                                                            disabled={submittingImages}
                                                                        />
                                                                        <label
                                                                            htmlFor={`image-upload-${index}`}
                                                                            className="cursor-pointer inline-flex flex-col items-center gap-2"
                                                                        >
                                                                            <Upload size={24} className="text-gray-400" />
                                                                            <span className="text-sm text-gray-600">
                                                                                Click to upload image
                                                                            </span>
                                                                            <span className="text-xs text-gray-500">
                                                                                JPEG, PNG, GIF, WebP (Max 5MB)
                                                                            </span>
                                                                        </label>
                                                                    </div>
                                                                )}
                                                                {imagesErrors[index]?.image && (
                                                                    <p className="text-red-500 text-xs mt-1">{imagesErrors[index].image}</p>
                                                                )}
                                                                {imagesErrors[index]?.is_primary && (
                                                                    <p className="text-red-500 text-xs mt-1">{imagesErrors[index].is_primary}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Submit Button */}
                                            {(imagesFormData.length > 0 || deletedImageIds.length > 0) && (
                                                <div className="flex justify-end mt-6">
                                                    <button
                                                        type="submit"
                                                        disabled={submittingImages}
                                                        className="bg-[#f4a406] hover:bg-[#d89005] text-white py-3 px-8 rounded-lg transition-colors inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        <Save size={18} />
                                                        {submittingImages ? 'Saving...' : `Save Images`}
                                                    </button>
                                                </div>
                                            )}
                                        </form>
                                    )}
                                </div>
                            )}

                            {/* PRODUCT ADDRESS TAB */}
                            {activeTab === 'ProductAddress' && (
                                <div key={'ProductAddress'} className="text-gray-700">
                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                        <div className="flex items-center gap-4">
                                            <button
                                                onClick={handleGoBack}
                                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                            >
                                                <ArrowLeft size={20} />
                                            </button>
                                            <div>
                                                <h1 className="text-2xl font-bold text-gray-900">
                                                    Product Pickup Addresses
                                                </h1>
                                            </div>
                                        </div>
                                        <button
                                            onClick={addNewAddressForm}
                                            className="bg-[#f4a406] hover:bg-[#d89005] text-white py-2 px-4 rounded-lg transition-colors inline-flex items-center gap-2"
                                        >
                                            <Plus size={18} />
                                            Add New Address
                                        </button>
                                    </div>

                                    {addressLoading ? (
                                        <div className="bg-white shadow-lg rounded-2xl p-6">
                                            <div className="space-y-4">
                                                <ShimmerLoader />
                                                <ShimmerLoader />
                                                <ShimmerLoader />
                                            </div>
                                        </div>
                                    ) : (
                                        <form onSubmit={handleAddressSubmit}>
                                            <div className="space-y-6 mt-4">
                                                {addressFormData.map((address, index) => (
                                                    <div
                                                        key={address.id || `new-address-${index}`}
                                                        className="bg-white shadow-lg rounded-2xl p-6 relative border-2 border-gray-200 transition-all duration-300"
                                                        data-card-type="address"
                                                    >
                                                        {/* Header section with delete button and badge */}
                                                        <div className="flex justify-between items-center mb-6">
                                                            {/* Delete button - Don't show if it's the only address */}
                                                            {addressFormData.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeAddressForm(index)}
                                                                    disabled={submittingAddress}
                                                                    className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            )}

                                                            {/* Spacer to push badge to right when delete button is hidden */}
                                                            {addressFormData.length <= 1 && <div></div>}

                                                            {/* Primary Address Badge */}
                                                            {address.is_primary && (
                                                                <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1">
                                                                    <MapPin size={12} />
                                                                    Primary
                                                                </span>
                                                            )}
                                                        </div>

                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                            {/* Title */}
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                    Title*
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    value={address.title}
                                                                    onChange={(e) => handleAddressInputChange(index, 'title', e.target.value)}
                                                                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#f4a406] focus:border-transparent ${addressErrors[index]?.title ? 'border-red-500' : 'border-gray-300'
                                                                        }`}
                                                                    placeholder="e.g., Main Office, Warehouse, Store"
                                                                    disabled={submittingAddress}
                                                                />
                                                                {addressErrors[index]?.title && (
                                                                    <p className="text-red-500 text-xs mt-1">{addressErrors[index].title}</p>
                                                                )}
                                                            </div>

                                                            {/* Is Primary */}
                                                            <div className="flex items-center">
                                                                <label className="flex items-center cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={address.is_primary}
                                                                        onChange={(e) => handleAddressInputChange(index, 'is_primary', e.target.checked)}
                                                                        disabled={submittingAddress}
                                                                        className="hidden"
                                                                    />
                                                                    <div className={`w-6 h-6 border-2 rounded-md mr-3 flex items-center justify-center transition-colors ${address.is_primary ? 'bg-[#f4a406] border-[#f4a406]' : 'border-gray-300'}`}>
                                                                        {address.is_primary && (
                                                                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                                                                            </svg>
                                                                        )}
                                                                    </div>
                                                                    <span className="text-sm font-medium text-gray-700">
                                                                        Primary Pickup Address
                                                                    </span>
                                                                </label>
                                                            </div>

                                                            {/* Address - Full width */}
                                                            <div className="md:col-span-2">
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                    Full Address*
                                                                </label>
                                                                <textarea
                                                                    value={address.address}
                                                                    onChange={(e) => handleAddressInputChange(index, 'address', e.target.value)}
                                                                    rows={3}
                                                                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#f4a406] focus:border-transparent ${addressErrors[index]?.address ? 'border-red-500' : 'border-gray-300'
                                                                        }`}
                                                                    placeholder="Enter complete pickup address including street, city, state, and zip code"
                                                                    disabled={submittingAddress}
                                                                />
                                                                {addressErrors[index]?.address && (
                                                                    <p className="text-red-500 text-xs mt-1">{addressErrors[index].address}</p>
                                                                )}
                                                            </div>

                                                            {/* Latitude */}
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                    Latitude
                                                                </label>
                                                                <input
                                                                    type="number"
                                                                    step="any"
                                                                    value={address.latitude || ''}
                                                                    onChange={(e) => handleAddressInputChange(index, 'latitude', e.target.value)}
                                                                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#f4a406] focus:border-transparent ${addressErrors[index]?.latitude ? 'border-red-500' : 'border-gray-300'
                                                                        }`}
                                                                    placeholder="e.g., 40.7128"
                                                                    disabled={submittingAddress}
                                                                />
                                                                {addressErrors[index]?.latitude && (
                                                                    <p className="text-red-500 text-xs mt-1">{addressErrors[index].latitude}</p>
                                                                )}
                                                            </div>

                                                            {/* Longitude */}
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                    Longitude
                                                                </label>
                                                                <input
                                                                    type="number"
                                                                    step="any"
                                                                    value={address.longitude || ''}
                                                                    onChange={(e) => handleAddressInputChange(index, 'longitude', e.target.value)}
                                                                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#f4a406] focus:border-transparent ${addressErrors[index]?.longitude ? 'border-red-500' : 'border-gray-300'
                                                                        }`}
                                                                    placeholder="e.g., -74.0060"
                                                                    disabled={submittingAddress}
                                                                />
                                                                {addressErrors[index]?.longitude && (
                                                                    <p className="text-red-500 text-xs mt-1">{addressErrors[index].longitude}</p>
                                                                )}
                                                            </div>

                                                            {addressErrors[index]?.is_primary && (
                                                                <div className="md:col-span-2">
                                                                    <p className="text-red-500 text-xs mt-1">{addressErrors[index].is_primary}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Submit Button */}
                                            {(addressFormData.length > 0 || deletedAddressIds.length > 0) && (
                                                <div className="flex justify-end mt-6">
                                                    <button
                                                        type="submit"
                                                        disabled={submittingAddress}
                                                        className="bg-[#f4a406] hover:bg-[#d89005] text-white py-3 px-8 rounded-lg transition-colors inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        <Save size={18} />
                                                        {submittingAddress ? 'Saving...' : `Save Addresses`}
                                                    </button>
                                                </div>
                                            )}
                                        </form>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}